import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Flex, List, Spinner, useScreenSize } from '@banhala/ui-kit';
import { AnalyticsScreen } from 'shared/src/context';
import {
  useBottomSheetRoute,
  useIsInFragmentScreens,
  useOnForeground,
} from 'shared/src/hooks';
import { useIsAnonymousUser } from 'shared/src/hooks/useIsAnonymousUser';
import { isWeb, useNavigator } from 'shared/src/utils';
import { systemAlert } from 'shared/src/component/systemAlert';
import { useDeleteFolder } from '../../queries/useDeleteFolder';
import { useLikedFolders } from '../../queries/useLikedFolders';
import type { Folder } from '../../types';
import { FolderItem } from './components/FolderItem';
import { FolderListHeader } from './components/FolderListHeader';
import { useCreateFolderBottomSheet } from './hooks/useCreateFolderBottomSheet';
import { useLogClickEditFolderOrder } from './hooks/useLogClickEditFolderOrder';
import { useLogClickFolder } from './hooks/useLogClickFolder';
import { useLogClickMakeNewFolder } from './hooks/useLogClickMakeNewFolder';
import { useLogClickRemoveFolder } from './hooks/useLogClickRemoveFolder';
import { useLogClickRenameFolder } from './hooks/useLogClickRenameFolder';
import { useLogRefreshLikedFolder } from './hooks/useLogRefreshLikedFolder';
import { useLogViewLikedFolder } from './hooks/useLogViewLikedFolder';
import { useRenameFolderBottomSheet } from './hooks/useRenameFolderBottomSheet';
import { FolderOrderEditModal } from '../FolderOrderEdit';
import { LikeScrollToTopButton } from '../LikeScrollToTopButton';
import { useScrollToTop } from '../../hooks/useScrollToTop';

const HORIZONTAL_PADDING = 16;
const COLUMN_GAP = 8;

interface LikeFolderProps {
  isActive: boolean;
}

export const LikeFolder = ({ isActive }: LikeFolderProps) => {
  return (
    <AnalyticsScreen screen="LIKED_FOLDER" hostsModals={!isWeb}>
      <LikeFolderContent isActive={isActive} />
    </AnalyticsScreen>
  );
};

const LikeFolderContent = ({ isActive }: LikeFolderProps) => {
  const {
    data,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
  } = useLikedFolders();
  const folders = useMemo(() => data ?? [], [data]);
  const [isPullRefreshing, setIsPullRefreshing] = useState(false);
  const [isOrderEditVisible, setIsOrderEditVisible] = useState(false);
  const showSpinner =
    isFetching && !isFetchingNextPage && !isPullRefreshing;
  const { navigator } = useNavigator();
  const { mutate: deleteFolder } = useDeleteFolder();
  const { show: showCreateFolderBottomSheet } = useCreateFolderBottomSheet();
  const { show: showRenameFolderBottomSheet } = useRenameFolderBottomSheet();
  const { open: openBottomSheetRoute } = useBottomSheetRoute();
  const isInFragmentScreens = useIsInFragmentScreens();

  useLogViewLikedFolder(isActive);
  const logRefreshLikedFolder = useLogRefreshLikedFolder();
  const logClickFolder = useLogClickFolder();
  const logClickMakeNewFolder = useLogClickMakeNewFolder();
  const logClickEditFolderOrder = useLogClickEditFolderOrder();
  const logClickRenameFolder = useLogClickRenameFolder();
  const logClickRemoveFolder = useLogClickRemoveFolder();

  const handlePullRefresh = useCallback(() => {
    logRefreshLikedFolder();
    setIsPullRefreshing(true);
    refetch().finally(() => setIsPullRefreshing(false));
  }, [logRefreshLikedFolder, refetch]);

  useOnForeground(
    useCallback(() => {
      refetch();
    }, [refetch]),
  );

  const prevIsActive = useRef(isActive);
  useEffect(() => {
    if (!prevIsActive.current && isActive) {
      refetch();
    }
    prevIsActive.current = isActive;
  }, [isActive, refetch]);
  const { width: screenWidth } = useScreenSize();
  const isAnonymous = useIsAnonymousUser();

  const itemWidth = useMemo(
    () => (screenWidth - HORIZONTAL_PADDING * 2 - COLUMN_GAP) / 2,
    [screenWidth],
  );

  const { listRef, scrollY, handleScroll, handleScrollToTop } =
    useScrollToTop<Folder>();

  const confirmDeleteFolder = useCallback((sno: number) => {
    systemAlert(
      '서랍 삭제',
      '삭제한 서랍은 복구할 수 없고\n찜한 상품도 함께 삭제됩니다.',
      [
        { text: '취소', style: 'cancel' },
        {
          text: '삭제',
          onPress: () => deleteFolder({ sno }),
        },
      ],
    );
  }, [deleteFolder]);

  const items = useMemo<Folder[]>(() => {
    const folderItems: Folder[] = folders.map(folder => ({
      action: 'open',
      folder,
    }));
    return isAnonymous
      ? folderItems
      : [{ action: 'create' }, ...folderItems];
  }, [folders, isAnonymous]);

  return (
    <Flex.Column flex={1}>
      {showSpinner && (
        <Flex.Center
          position={'absolute'}
          top={0}
          bottom={0}
          left={0}
          right={0}
          zIndex={1}
        >
          <Spinner size={20} />
        </Flex.Center>
      )}
      <List<Folder>
        ref={listRef}
        data={items}
        onScroll={handleScroll}
        scrollEventThrottle={16}
        numColumns={2}
        columnWrapperStyle={{ gap: COLUMN_GAP }}
        contentContainerStyle={{
          paddingHorizontal: HORIZONTAL_PADDING,
          paddingVertical: 16,
          gap: 12,
        }}
        refreshing={isPullRefreshing}
        onRefresh={handlePullRefresh}
        ListHeaderComponent={
          <FolderListHeader
            totalCount={folders.length}
            onPressEditOrder={() => {
              logClickEditFolderOrder();
              setIsOrderEditVisible(true);
            }}
          />
        }
        keyExtractor={item =>
          item.action === 'create' ? 'create' : item.folder.sno.toString()
        }
        onEndReached={() => {
          if (hasNextPage) fetchNextPage();
        }}
        onEndReachedThreshold={1}
        renderItem={({ item, index }) => (
          <FolderItem
            folder={item}
            folderIdx={isAnonymous ? index : index - 1}
            width={itemWidth}
            onPressCreate={() => {
              logClickMakeNewFolder();
              if (isInFragmentScreens) {
                openBottomSheetRoute('create-folder', undefined);
              } else {
                showCreateFolderBottomSheet();
              }
            }}
            onPressDelete={folder => {
              logClickRemoveFolder({ folderSno: folder.sno });
              confirmDeleteFolder(folder.sno);
            }}
            onPressRename={folder => {
              logClickRenameFolder({ folderSno: folder.sno });
              const renameParam = {
                sno: folder.sno,
                initialTitle: folder.title,
                coverImage: folder.cover_image,
              };
              if (isInFragmentScreens) {
                openBottomSheetRoute('rename-folder', renameParam);
              } else {
                showRenameFolderBottomSheet(renameParam);
              }
            }}
            onPressOpen={folder => {
              logClickFolder({ folderSno: folder.sno });
              navigator.moveToLikeFolders({ folderSno: folder.sno });
            }}
          />
        )}
      />
      <LikeScrollToTopButton scrollY={scrollY} onPress={handleScrollToTop} />
      <FolderOrderEditModal
        visible={isOrderEditVisible}
        onClose={() => setIsOrderEditVisible(false)}
      />
    </Flex.Column>
  );
};
